import * as $ from "jquery";
import * as R from "ramda";
import * as targetEnv from "targetenv";


/**
 * Server-generated configuration of this client application (not user-specific).
 */
export interface PliConfig {
    /**
     * Version of server application that generated the page that loaded this script.
     * Example: "42.2.4.1024".
     */
    appVersion: string;

    /**
     * Absolute path (relative to the server) of root of current context/installation.
     * Example: "/SamplePliWeb/usa/" (on development machine), "/usa/" (in production).
     * When null/undefined, same as "rootPath".
     */
    contextRootPath?: string;

    /**
     * A flag indicating whether to look for parent Pli during construction (false, default) or not (true).
     */
    ignoreParentPli?: boolean;

    /**
     * A flag indicating whether this code is running in a browser (true) or not (false).
     * (example of a non-browser environment is nodejs + mocha).
     */
    isBrowser: boolean;

    /**
     * An absolute path (relative to the server) of web application root (equivalent to ASP.NET's "~").
     * Example: "/SamplePliWeb/" (on development machine), "/" (in production).
     */
    rootPath: string;
}


/**
 * Container for properties and methods that are visible in all child windows
 * (without having to traverse parent hierarchy). Available as "Pli.global".
 *
 * STRONGLY DEPRECATED.
 * TODO: delete as part of the next major release
 */
class PliGlobal {
    config: any;
    rootPli: any;
    resolveUrl: any;
}


/**
 * Describes Pli system used by this client application.
 */
export class PliSingleton {

    /**
     * Creates a new instance of Pli singleton for the current window.
     * This Pli instance will be chained to a parent Pli instance (if any).
     *
     * Each window will have exactly one instance of this class,
     * available via "Pli" global variable (this has been deprecated,
     * please use imports instead).
     */
    constructor(loaderWindow: any, config: PliConfig) {
        this.config = config || ({ appVersion: "", isBrowser: false, rootPath: "" } as PliConfig);
        this.config.appVersion = loaderWindow.PliAppVersion || "";
        this.config.isBrowser = targetEnv.isBrowser;
        this.config.rootPath = this.config.rootPath || "";
        this.loaderWindow = loaderWindow;
        this.parentPli = (this.config.ignoreParentPli ? null : findAlreadyDefinedPli(loaderWindow));
        this.rootPli = (this.parentPli ? this.parentPli.rootPli : this);

        // setup prototype ihneritance chain to parent global object; STRONGLY DEPRECATED
        // TODO: delete as part of the next major release
        if (this.parentPli) { // this is a chained PLI (we have a parent)
            PliGlobal.prototype = this.parentPli.global;
            this.global = new PliGlobal();
            if (!this.global.config) { // because prototypal inheritance doesn't seem to work in IE, copy parent's global properties to our global object instead
                this.global = R.merge(this.global, this.parentPli.global || {});
            }
        } else { // this is a root PLI
            this.global = new PliGlobal();
            this.global.rootPli = this;
            this.global.config = R.clone(this.config); // make a copy of window-specific config
            PliGlobal.prototype.resolveUrl = this.resolveUrl;
        }
    }

    /**
     * Gets server-generated configuration of this client application (not user-specific).
     * Often passes values from hierarchical configuration down to clients.
     */
    config: PliConfig;

    /**
     * Recursively searches the parent instances looking for one that has the specified property (ignores current instance).
     * @returns {T}
     *     The found component (value of the specified property) or null.
     */
    findComponent<T>(key: string): T {
        const parentPli = this.parentPli;
        return parentPli ? ((parentPli as any)[key] || parentPli.findComponent(key)) : null;
    }

    /**
     * Gets reference to global configuration visible in all child windows. STRONGLY DEPRECATED, DO NOT USE.
     */
    private global: PliGlobal;

    /**
     * Gets reference to the "window/global" object that created this singleton instance.
     */
    loaderWindow: any;

    /**
     * Gets the reference to Pli singleton in parent browser window/frame/tab.
     * Set to null in root window/frame/tab or if "config.ignoreParentPli" is truthy.
     */
    parentPli: PliSingleton;

    /**
     * Resolves relative URL path to an absolute path (recognizes "~" and "~~").
     * - "~" is root of the web application
     * - "~~" is root of the current context/installation (can be the same as "~")
     *
     * Example:
     *
     * If rootPath is "/mysite/" and contextRootPath is not defined:
     * - resolveUrl("~/Page.html") => "/mysite/Page.html"
     * - resolveUrl("~~/Page.html") => "/mysite/Page.html"
     *
     * If root path is "/mysite/" and contextRootPath is "/mysite/japan"
     * - resolveUrl("~/Page.html") => "/mysite/Page.html"
     * - resolveUrl("~~/Page.html") => "/mysite/japan/Page.html"
     */
    resolveUrl(url: string): string {
        let index = 1;
        let contextRootPath: string;

        if (url.length > 0 && url.charAt(0) === "~") {
            if (url.charAt(1) === "~") {
                index = 2;
                contextRootPath = this.config.contextRootPath;
            }
            if (url.length > index && (url.charAt(index) === "/" || url.charAt(index) === "\\")) {
                index = index + 1;
            }
            return (contextRootPath
                ? this.resolveUrl(contextRootPath.concat(url.slice(index)))
                : (this.config.rootPath || "").concat(url.slice(index)));
        }
        return url;
    }

    /**
     * Gets the reference to Pli singleton in root browser window/frame/tab.
     * (will be equal to "this" when "parentPli === null").
     */
    rootPli: PliSingleton;

}


/**
 * Recursively traverses all parent and opener windows, looking for window.Pli object.
 * Returns the closest window.Pli object or null if not found.
 */
function findAlreadyDefinedPli(wnd: any): PliSingleton {
    let found: any;
    try { // protect access to wnd.Pli - can sometimes throw "Access Denied" in IE
        found = wnd.Pli;
        if (found) {
            return found;
        }
    } catch (ex) { // there is a "Pli" property on this parent but we can't access it
        return null;
    }
    if (wnd.parent && wnd.parent !== wnd) {
        found = findAlreadyDefinedPli(wnd.parent);
        if (found) {
            return found;
        }
    }
    if (wnd.opener) {
        found = findAlreadyDefinedPli(wnd.opener);
        if (found) {
            return found;
        }
    }
    if (wnd.dialogArguments) {
        if (wnd.dialogArguments.parentPli) {
            return wnd.dialogArguments.parentPli;
        }
        if (wnd.dialogArguments.Window) {
            return findAlreadyDefinedPli(wnd.dialogArguments.Window);
        }
    }
    return null;
}


export const pli = new PliSingleton(
    global,
    targetEnv.isBrowser ? $("body").data("pliConfig") : null);



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/core/pli.ts